import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useActiveBling, blingLabel } from "@/lib/blingContext";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Search, Package, ImageIcon, AlertTriangle,
} from "lucide-react";

const brl = (v) => (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const kg = (v) => (v === null || v === undefined) ? "—" : `${Number(v).toFixed(3).replace(".", ",")} kg`;

export default function Catalog() {
  const { activeBling } = useActiveBling();
  const activeBlingId = activeBling?.id || "";
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      // Multi-Bling isolation: only fetch products of the active Bling.
      // If no Bling is connected yet the caller sees an empty catalog
      // rather than a union across accounts.
      const params = {};
      if (activeBlingId) params.integration_id = activeBlingId;
      const { data } = await api.get("/bling/catalog", { params });
      setItems(data.items || []);
      setMeta({
        last_sync_at: data.last_sync_at,
        last_sync_count: data.last_sync_count,
        last_incremental_sync_at: data.last_incremental_sync_at,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar catálogo");
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever the user switches the active Bling in the sidebar.
  useEffect(() => { fetchCatalog(); }, [activeBlingId]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const params = {};
      if (activeBlingId) params.integration_id = activeBlingId;
      const { data } = await api.post("/bling/catalog/sync", null, { params });
      toast.success(
        activeBling
          ? `Sincronizado (${blingLabel(activeBling)}): ${data.created} novos + ${data.updated} atualizados`
          : `Sincronizado! ${data.created} novos + ${data.updated} atualizados`
      );
      fetchCatalog();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((p) =>
      (p.nome || "").toLowerCase().includes(s) ||
      (p.codigo || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  const lastSyncLabel = meta.last_sync_at
    ? new Date(meta.last_sync_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "nunca";
  const lastIncrLabel = meta.last_incremental_sync_at
    ? new Date(meta.last_incremental_sync_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="space-y-4" data-testid="catalog-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Package className="w-3 h-3" /> Bling
          </div>
          <h1 className="text-3xl font-black tracking-tighter">Catálogo de Produtos</h1>
          <p className="text-sm text-slate-600 mt-1">
            Última sincronização completa: <b>{lastSyncLabel}</b>
            {meta.last_sync_count ? ` · ${meta.last_sync_count} itens` : ""}
          </p>
          {lastIncrLabel && (
            <p className="text-xs text-slate-500 mt-0.5" data-testid="catalog-last-incremental">
              Auto-sync incremental (a cada 10 min): <b>{lastIncrLabel}</b>
            </p>
          )}
        </div>
        <button
          data-testid="catalog-sync"
          onClick={syncNow}
          disabled={syncing}
          className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white inline-flex items-center gap-2 disabled:opacity-60"
          style={{ background: "#002EA6" }}
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Sincronizar com Bling
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
        <input
          data-testid="catalog-search"
          type="text"
          placeholder="Buscar por nome ou SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
        />
      </div>

      <div className="border border-slate-200 bg-white rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            {items.length === 0
              ? <>Catálogo vazio. Clique em <b>Sincronizar com Bling</b> para importar seus produtos.</>
              : "Nenhum produto encontrado com esse termo."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 border-b border-slate-200 sticky top-0">
              <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                <th className="px-3 py-2 text-left w-14">Img</th>
                <th className="px-3 py-2 text-left">Nome</th>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-right">Custo</th>
                <th className="px-3 py-2 text-left">Dimensões (A×L×C cm)</th>
                <th className="px-3 py-2 text-right">Peso</th>
                <th className="px-3 py-2 text-right">Peso Cúbico</th>
                <th className="px-3 py-2 text-right">Peso p/ Frete</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="catalog-tbody">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2">
                    {p.imagem_url ? (
                      <img src={p.imagem_url} alt={p.nome} className="w-10 h-10 object-cover rounded border border-slate-200" />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 border border-slate-200 rounded flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-bold text-slate-900 line-clamp-1 max-w-[260px]" title={p.nome}>{p.nome}</div>
                    {p.dimensoes_incompletas && (
                      <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-sm">
                        <AlertTriangle className="w-2.5 h-2.5" /> Dimensões incompletas
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{p.codigo || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{brl(p.preco_custo)}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">
                    {p.altura_cm > 0 || p.largura_cm > 0 || p.comprimento_cm > 0
                      ? `${p.altura_cm} × ${p.largura_cm} × ${p.comprimento_cm}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{kg(p.peso_kg)}</td>
                  <td className="px-3 py-2 text-right font-mono">{kg(p.peso_cubico_kg)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-[#002EA6]">{kg(p.peso_frete_kg)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-xs text-slate-500 text-center py-2">
        Peso cúbico calculado como <b>(A × L × C) / 6000</b>. Peso para frete = maior entre real e cúbico.
      </div>
    </div>
  );
}
